<template>
  <div class="slideshow-page">
    <div class="slideshow-container">
      <!-- Photo Display -->
      <div class="photo-wrapper">
        <img
          v-if="currentPhoto"
          :src="currentPhoto.url"
          :alt="currentPhoto.title || 'Memory photo'"
          class="photo"
          :class="{ 'fade-out': isFading }"
        />
      </div>

      <!-- Caption -->
      <div
        v-if="currentPhoto && currentPhoto.title"
        class="caption"
        :class="{ 'caption-clickable': isCaptionTruncated && !captionExpanded, 'caption-expanded': captionExpanded }"
        @click="isCaptionTruncated && !captionExpanded ? captionExpanded = true : null"
      >
        <template v-if="!captionExpanded">
          {{ truncatedCaption }}<span v-if="isCaptionTruncated" class="read-more-link">&hellip; Read more</span>
        </template>
        <template v-else>
          {{ currentPhoto.title }}
          <div class="minimise-row">
            <button class="minimise-link" @click.stop="captionExpanded = false">Minimise</button>
          </div>
        </template>
      </div>

      <!-- Controls -->
      <div class="controls">
        <div class="control-buttons">
          <button @click="previousPhoto" class="control-btn" title="Previous">
            <v-icon>mdi-chevron-left</v-icon>
          </button>

          <button @click="togglePause" class="control-btn" :title="isPaused ? 'Play' : 'Pause'">
            <v-icon>{{ isPaused ? 'mdi-play' : 'mdi-pause' }}</v-icon>
          </button>

          <button @click="nextPhoto" class="control-btn" title="Next">
            <v-icon>mdi-chevron-right</v-icon>
          </button>
        </div>

        <!-- Speed Control -->
        <div class="speed-control">
          <label class="speed-label">Speed:</label>
          <div class="speed-options">
            <label v-for="speed in speedOptions" :key="speed" class="speed-option">
              <input
                type="radio"
                name="speed"
                :value="speed"
                v-model="slideSpeed"
                @change="onSpeedChange"
              />
              <span>{{ speed }}</span>
            </label>
          </div>
        </div>

        <button @click="closeSlideshow" class="control-btn close-btn" title="Close">
          <v-icon>mdi-close</v-icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'

export default {
  name: 'SlideShowPage',
  setup() {
    const router = useRouter()
    const store = useMemorialStore()

    const photos = ref([])
    const currentIndex = ref(0)
    const isPaused = ref(false)
    const isFading = ref(false)
    const slideSpeed = ref(3) // default 3 seconds
    const speedOptions = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1]
    const intervalId = ref(null)
    const loading = ref(true)
    const captionExpanded = ref(false)

    const currentPhoto = computed(() => {
      return photos.value[currentIndex.value] || null
    })

    const isCaptionTruncated = computed(() => {
      const title = currentPhoto.value?.title
      if (!title) return false
      return title.trim().split(/\s+/).length > 17
    })

    const truncatedCaption = computed(() => {
      const title = currentPhoto.value?.title
      if (!title) return ''
      const words = title.trim().split(/\s+/)
      if (words.length <= 17) return title
      return words.slice(0, 15).join(' ')
    })

    watch(currentIndex, () => {
      captionExpanded.value = false
    })

    const loadPhotos = async () => {
      loading.value = true
      try {
        // Fetch memorial data to get photos and contributions
        const response = await apiService.getMemorial(store.memorialUuid)

        if (response.success && response.memorial) {
          const memorial = response.memorial
          const photoList = []

          // Add memorial profile photo first
          if (memorial.profilePhoto) {
            const profile = memorial.profilePhoto
            const url = profile.location
              ? `${profile.location}/${profile.filename}.${profile.extension}`
              : `https://assets.forever-x.com/photos/${profile.filename}.${profile.extension}`

            photoList.push({
              url: url,
              title: profile.title || '',
              type: 'profile'
            })
          }

          // Add memorial photos
          if (memorial.media && memorial.media.length > 0) {
            memorial.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
              const url = media.location
                ? `${media.location}/${media.filename}.${media.extension}`
                : `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`

              photoList.push({
                url: url,
                title: media.title || '',
                type: 'memorial'
              })
            })
          }

          // Add contribution photos
          if (memorial.contributions && memorial.contributions.length > 0) {
            memorial.contributions.forEach(contribution => {
              if (contribution.media && contribution.media.length > 0) {
                contribution.media.filter(media => media.mediaType === 0 || media.mediaType == null).forEach(media => {
                  const url = media.location
                    ? `${media.location}/${media.filename}.${media.extension}`
                    : `https://assets.forever-x.com/photos/${media.filename}.${media.extension}`

                  photoList.push({
                    url: url,
                    title: media.title || '',
                    type: 'contribution',
                    contributorName: contribution.contributorName || 'Anonymous'
                  })
                })
              }
            })
          }

          photos.value = photoList

          if (photos.value.length > 0) {
            startSlideshow()
          }
        }
      } catch (error) {
        console.error('Error loading photos:', error)
      } finally {
        loading.value = false
      }
    }

    const startSlideshow = () => {
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }

      if (!isPaused.value) {
        intervalId.value = setInterval(() => {
          advanceSlide()
        }, slideSpeed.value * 1000)
      }
    }

    const advanceSlide = () => {
      if (photos.value.length === 0) return

      // Fade out current photo
      isFading.value = true

      setTimeout(() => {
        // Move to next photo
        currentIndex.value = (currentIndex.value + 1) % photos.value.length

        // Fade in new photo
        setTimeout(() => {
          isFading.value = false
        }, 50)
      }, 300) // 300ms fade duration
    }

    const nextPhoto = () => {
      if (photos.value.length === 0) return

      // Reset interval
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }

      advanceSlide()

      if (!isPaused.value) {
        startSlideshow()
      }
    }

    const previousPhoto = () => {
      if (photos.value.length === 0) return

      // Reset interval
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }

      // Fade out current photo
      isFading.value = true

      setTimeout(() => {
        // Move to previous photo
        currentIndex.value = currentIndex.value === 0
          ? photos.value.length - 1
          : currentIndex.value - 1

        // Fade in new photo
        setTimeout(() => {
          isFading.value = false
        }, 50)
      }, 300)

      if (!isPaused.value) {
        startSlideshow()
      }
    }

    const togglePause = () => {
      isPaused.value = !isPaused.value

      if (isPaused.value) {
        if (intervalId.value) {
          clearInterval(intervalId.value)
          intervalId.value = null
        }
      } else {
        startSlideshow()
      }
    }

    const onSpeedChange = () => {
      // Restart slideshow with new speed
      if (!isPaused.value) {
        startSlideshow()
      }
    }

    const closeSlideshow = () => {
      router.push('/memory/dashboard')
    }

    onMounted(() => {
      loadPhotos()
    })

    onUnmounted(() => {
      if (intervalId.value) {
        clearInterval(intervalId.value)
      }
    })

    return {
      photos,
      currentPhoto,
      isCaptionTruncated,
      truncatedCaption,
      captionExpanded,
      currentIndex,
      isPaused,
      isFading,
      slideSpeed,
      speedOptions,
      loading,
      nextPhoto,
      previousPhoto,
      togglePause,
      onSpeedChange,
      closeSlideshow
    }
  }
}
</script>

<style scoped>
.slideshow-page {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--v-theme-black);
  z-index: 9999;
  overflow: hidden;
}

.slideshow-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.photo-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 60px 20px 120px;
}

.photo {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  opacity: 1;
  transition: opacity 0.3s ease-in-out;
}

.photo.fade-out {
  opacity: 0;
}

.caption {
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  color: #000000;
  font-size: 18px;
  text-align: center;
  padding: 10px 20px;
  background-color: #fffc;
  border-radius: 4px;
  max-width: 80%;
  white-space: pre-wrap;
}

.caption-expanded {
  max-width: min(95vw, 900px);
  max-height: calc(100vh - 120px);
  overflow-y: auto;
}

.caption-clickable {
  cursor: pointer;
}

.read-more-link {
  font-weight: 600;
  color: #0055cc;
  margin-left: 2px;
}

.minimise-row {
  margin-top: 8px;
  text-align: center;
}

.minimise-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0055cc;
  cursor: pointer;
  text-decoration: underline;
}

.controls {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.8);
  padding: 15px 20px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 30px;
}

.control-buttons {
  display: flex;
  gap: 15px;
  align-items: center;
}

.close-btn {
  margin-left: 30px;
}

.control-btn {
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: var(--v-theme-surface);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.control-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

.control-btn:active {
  transform: scale(0.95);
}

.speed-control {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ffffff;
}

.speed-label {
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
}

.speed-options {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
}

.speed-option {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 14px;
}

.speed-option input[type="radio"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin: 0;
}

.speed-option span {
  user-select: none;
  display: none;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
  .caption {
    font-size: 14px;
    bottom: 90px;
  }

  .controls {
    padding: 10px;
    flex-wrap: wrap;
    gap: 15px;
  }

  .control-btn {
    width: 40px;
    height: 40px;
  }

  .close-btn {
    margin-left: 0;
  }

  .speed-control {
    gap: 8px;
  }

  .speed-options {
    gap: 6px;
  }

  .speed-option input[type="radio"] {
    width: 14px;
    height: 14px;
  }
}

/* Safari on iOS: add extra bottom padding to clear the address bar */
@media (max-width: 600px) {
  @supports (-webkit-touch-callout: none) {
    .controls {
      padding-bottom: env(safe-area-inset-bottom, 20px);
    }
  }
}
</style>
